import { dayKey } from "./expense-list";
import { RANGE } from "./report-filter";

export const DAY_BUCKET_MAX_DAYS = 31;
export const WEEK_BUCKET_MAX_DAYS = 26 * 7;

export const BUCKET = { day: "day", week: "week", month: "month" };

export { RANGE };

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

export const isDayKey = (value) =>
  typeof value === "string" &&
  DAY_KEY_PATTERN.test(value) &&
  !Number.isNaN(Date.parse(`${value}T12:00:00Z`)) &&
  new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;

export const spentAtOf = (expense) => {
  const time = Date.parse(expense?.spent_at ?? expense?.created_at);
  return Number.isNaN(time) ? null : time;
};

export const spentDayOf = (expense) => {
  const time = spentAtOf(expense);
  return time === null ? null : dayKey(time);
};

// Calendar arithmetic on noon UTC, so DST changes never shift a day.
const noonOf = (key) => Date.parse(`${key}T12:00:00Z`);
const keyOf = (time) => new Date(time).toISOString().slice(0, 10);

export const addDays = (key, days) => keyOf(noonOf(key) + days * MS_PER_DAY);

export const daysBetween = (fromKey, toKey) =>
  Math.round((noonOf(toKey) - noonOf(fromKey)) / MS_PER_DAY);

export const weekStartOf = (key) => {
  const weekday = (new Date(noonOf(key)).getUTCDay() + 6) % 7;
  return addDays(key, -weekday);
};

export const monthStartOf = (key) => `${key.slice(0, 7)}-01`;

const nextMonth = (key) => {
  const date = new Date(noonOf(monthStartOf(key)));
  date.setUTCMonth(date.getUTCMonth() + 1);
  return keyOf(date.getTime());
};

export const rangeBounds = (range, now = new Date()) => {
  const today = dayKey(now);
  switch (range?.kind) {
    case RANGE.last7:
      return { from: addDays(today, -6), to: null };
    case RANGE.last30:
      return { from: addDays(today, -29), to: null };
    case RANGE.custom: {
      const from = isDayKey(range.from) ? range.from : null;
      const to = isDayKey(range.to) ? range.to : null;
      if (from && to && from > to) return { from: to, to: from };
      return { from, to };
    }
    default:
      return { from: null, to: null };
  }
};

export const isInRange = (key, { from, to }) => {
  if (!from && !to) return true;
  if (key === null) return false;
  return (!from || key >= from) && (!to || key <= to);
};

export const bucketSizeFor = (spanDays) => {
  if (spanDays <= DAY_BUCKET_MAX_DAYS) return BUCKET.day;
  if (spanDays <= WEEK_BUCKET_MAX_DAYS) return BUCKET.week;
  return BUCKET.month;
};

export const bucketStartOf = (key, size) => {
  if (size === BUCKET.week) return weekStartOf(key);
  if (size === BUCKET.month) return monthStartOf(key);
  return key;
};

const nextBucketStart = (start, size) => {
  if (size === BUCKET.week) return addDays(start, 7);
  if (size === BUCKET.month) return nextMonth(start);
  return addDays(start, 1);
};

const utcFormat = (options) =>
  new Intl.DateTimeFormat("de-DE", { timeZone: "UTC", ...options });

const shortDay = utcFormat({ day: "2-digit", month: "2-digit" });
const fullDay = utcFormat({
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const weekdayDay = utcFormat({
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});
const shortMonth = utcFormat({ month: "short" });
const shortMonthYear = utcFormat({ month: "short", year: "2-digit" });
const longMonth = utcFormat({ month: "long", year: "numeric" });

export const formatDay = (key) => (key ? fullDay.format(noonOf(key)) : "");

export const formatDayRange = (fromKey, toKey) => {
  if (!fromKey) return "";
  if (!toKey || fromKey === toKey) return formatDay(fromKey);
  return `${formatDay(fromKey)} – ${formatDay(toKey)}`;
};

const bucketLabels = (start, end, size, spansYears) => {
  if (size === BUCKET.month) {
    return {
      label: (spansYears ? shortMonthYear : shortMonth).format(noonOf(start)),
      longLabel: longMonth.format(noonOf(start)),
    };
  }
  if (size === BUCKET.week) {
    return {
      label: shortDay.format(noonOf(start)),
      longLabel: `Woche ${formatDayRange(start, end)}`,
    };
  }
  return {
    label: shortDay.format(noonOf(start)),
    longLabel: weekdayDay.format(noonOf(start)),
  };
};

export const buildBuckets = (fromKey, toKey, size) => {
  if (!fromKey || !toKey || fromKey > toKey) return [];
  const spansYears = fromKey.slice(0, 4) !== toKey.slice(0, 4);
  const buckets = [];
  for (
    let start = bucketStartOf(fromKey, size);
    start <= toKey;
    start = nextBucketStart(start, size)
  ) {
    const end = addDays(nextBucketStart(start, size), -1);
    buckets.push({
      key: start,
      start,
      end,
      ...bucketLabels(start, end, size, spansYears),
      total: 0,
      count: 0,
    });
  }
  return buckets;
};
